import React, { useReducer, useContext, useCallback } from "react"
import EmployeeAccountReducer from 'employee/state/EmployeeAccountReducer';

import { Commands } from 'shared/resources/BitbiteCommands';
import { SessionContext } from 'shared/system-controls/session/SessionProvider';

export const EmployeeAccountContext = React.createContext();

const EmployeeAccountState = ({ children }) => {
	const initialState = {
		filters: { actions: [] },
		activityLogs: [],
	}
	const [state, dispatch] = useReducer(EmployeeAccountReducer, initialState);
	const { asyncServiceCall } = useContext(SessionContext);

	//----------------------------------------------------------
	const getAccounts = useCallback(async ({ filters, pageNo, count, showArchive }) => {
		// console.log(filters)
		try {
			dispatch({ type: 'IN_LOADING' });
			filters = filters || {};
			count = count || 5000;

			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_EMPLOYEE_ACCOUNT_LIST, {
				...filters,
				first: pageNo * count || 0,
				count: count,
				state: showArchive ? undefined : 'ACCOUNT_STATE_ACTIVE',
			});
			// console.log(res);
			dispatch({ type: 'SET_STATE_DATA', payload: res });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//----------------------------------------------------------
	const checkNationalID = useCallback(async ({ nationalID }) => {
		// console.log(filters)
		try {
			dispatch({ type: 'IN_LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_EMPLOYEE_ACCOUNT_LIST, {
				filters: { nationalID: nationalID },
			});
			dispatch({ type: 'LOADED' });
			if (res.users?.length > 0) {
				dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: `کد ملی تکراری ست.(${res.users[0].profile?.firstname || ''} ${res.users[0].profile?.lastname || res.users[0].username})`, type: 'danger' }) });
			}
			else {
				dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'کد ملی تکراری نیست.', type: 'success' }) });
			}
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//--------------------------------------------
	const getAccount = useCallback(async ({ userID }) => {
		try {
			if (!userID) {
				return;
			}
			dispatch({ type: 'IN_LOADING' });
			let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_EMPLOYEE_ACCOUNT_GET(userID), {});
			dispatch({ type: 'SET_STATE_DATA', payload: res });
			dispatch({ type: 'LOADED' });
		}
		catch (err) {
			console.log(err);
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall]);

	//----------------------------------------------------------------
	const setUser = useCallback(async ({ userID, profile, showArchive, callback }) => {
		try {
			if (!profile) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			let res = await asyncServiceCall(userID ? Commands.API_URL_BITBITE_BO_EMPLOYEE_ACCOUNT_SET(userID) : Commands.API_URL_BITBITE_BO_EMPLOYEE_ACCOUNT_NEW, {
				profile: profile,
			});
			dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'اطلاعات با موفقیت ثبت شد.', type: 'success' }) });
			setTimeout(() => getAccounts({ showArchive }), 2000);
			setTimeout(() => dispatch({ type: 'SET_STATE_DATA', payload: { user: res.user } }), 2000);
			callback && callback();
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccounts])

	//----------------------------------------------------------------
	const setPassword = useCallback(async ({ userID, password, callback }) => {
		try {
			if (!userID || !password) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_EMPLOYEE_PASSWORD_SET(userID), {
				password,
			});
			dispatch({ type: 'LOADED' });
			callback && callback();
			getAccount({ userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccount])

	//----------------------------------------------------------------
	const getUserSessions = useCallback(async ({ userID }) => {
		try {
			if (!userID) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_EMPLOYEE_SESSIONS_GET(userID), {});
			dispatch({ type: 'LOADED' });
			dispatch({ type: 'SET_STATE_DATA', payload: res });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall])

	//----------------------------------------------------------------
	const closeUserSession = useCallback(async ({ userID, token }) => {
		try {
			if (!userID || !token) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_SESSION_DELETE(userID), { sessionToken: token });
			dispatch({ type: 'LOADED' });
			getUserSessions({ userID });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getUserSessions])

	//----------------------------------------------------------------
	const closeAccount = useCallback(async ({ userID, showArchive }) => {
		try {
			if (!userID) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_ACCOUNT_CLOSE(userID));
			getAccounts({ showArchive });
			dispatch({ type: 'SET_STATE_DATA', payload: { user: undefined } });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccounts])

	//----------------------------------------------------------------
	const activateAccount = useCallback(async ({ userID, showArchive }) => {
		try {
			if (!userID) {
				return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
			}
			dispatch({ type: 'LOADING' });
			await asyncServiceCall(Commands.API_URL_BITBITE_BO_USER_ACCOUNT_ACTIVATE(userID));
			getAccounts({ showArchive });
			dispatch({ type: 'SET_STATE_DATA', payload: { user: undefined } });
		}
		catch (err) {
			dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
		}
	}, [asyncServiceCall, getAccounts])

	return (
		<EmployeeAccountContext.Provider
			value={{
				...state,
				state,
				dispatch,
				getAccounts,
				getAccount,
				setUser,
				checkNationalID,
				setPassword,
				getUserSessions,
				closeUserSession,
				closeAccount,
				activateAccount,
			}}
		>
			{children}
		</EmployeeAccountContext.Provider>
	)
}

export default EmployeeAccountState
